import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import { Bot, Edit2, Eye } from 'lucide-react';
import remarkGfm from 'remark-gfm';

import * as prdReviewApi from '~/services/api/prd-review';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { Textarea } from '~/components/Field';
import { Modal } from '~/components/Modal';

type ViewTab = 'view' | 'edit';

interface PromptModalProps {
  isOpen: boolean;
  title: string;
  prompt: string | null | undefined;
  onSave: (prompt: string) => void;
  onCancel: () => void;
  /**
   * Optional project ID. When provided, enables "Review PRD with AI" button
   * if a PRD document exists for the project.
   */
  projectId?: string;
  /** Loading state when prompt is being fetched (e.g. lazy-loaded from API). */
  isLoading?: boolean;
  /** Error when loading prompt failed. */
  loadError?: unknown;
}

export function PromptModal(props: PromptModalProps): React.JSX.Element | null {
  const {
    isLoading = false,
    isOpen,
    loadError,
    onCancel,
    onSave,
    projectId,
    prompt,
    title,
  } = props;
  const [localPrompt, setLocalPrompt] = useState(prompt ?? '');
  const [activeTab, setActiveTab] = useState<ViewTab>('view');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasUserEditedRef = useRef(false);
  /** Ref holds latest prompt for save; avoids stale closure when user types then immediately clicks Save. */
  const latestPromptRef = useRef(prompt ?? '');
  const navigate = useNavigate();

  const isDirty = localPrompt !== (prompt ?? '');

  // Minimal sync when prompt loads async (only if user hasn't edited)
  useEffect(() => {
    if (prompt !== undefined && !hasUserEditedRef.current) {
      const value = prompt ?? '';
      setLocalPrompt(value);
      latestPromptRef.current = value;
    }
  }, [prompt]);

  // Check if PRD document exists for this project (only when projectId is provided)
  const { data: prdDocument } = useQuery({
    enabled: isOpen && !!projectId,
    queryFn: () => prdReviewApi.getPRDContent(projectId ?? ''),
    queryKey: ['prd-document', projectId],
    retry: false, // Don't retry on 404 (PRD doesn't exist)
    staleTime: 30000, // 30 seconds
  });

  const handleChangePrompt = useCallback((value: string) => {
    hasUserEditedRef.current = true;
    latestPromptRef.current = value;
    setLocalPrompt(value);
  }, []);

  const handleSave = useCallback(() => {
    // Read from ref to avoid stale closure when user types then immediately clicks Save
    const valueToSave =
      activeTab === 'edit' && textareaRef.current
        ? textareaRef.current.value
        : latestPromptRef.current;
    onSave(valueToSave);
  }, [activeTab, onSave]);

  const handleTabChange = useCallback((tab: ViewTab) => {
    setActiveTab(tab);
    if (tab === 'edit') {
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, []);

  const handleCloseRequest = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onCancel();
    }
  }, [isDirty, onCancel]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardConfirm(false);
    onCancel();
  }, [onCancel]);

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirm(false);
  }, []);

  const handleReviewPRD = useCallback((): void => {
    if (projectId) {
      onCancel(); // Close the modal first
      void navigate(`/projects/${projectId}/review`);
    }
  }, [navigate, onCancel, projectId]);

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid="PromptModal">
      {showDiscardConfirm && (
        <ConfirmationDialog
          cancelText="Keep Editing"
          confirmText="Discard"
          content={<p>You have unsaved changes. Discard?</p>}
          isOpen={showDiscardConfirm}
          onClose={handleDiscardCancel}
          onConfirm={handleDiscardConfirm}
          title="Unsaved changes"
        />
      )}
      {isOpen && (
        <Modal isOpen={isOpen} onClose={handleCloseRequest} preventClosing={isDirty} size="4xl">
          <ModalContent>
            <ModalHeader className="flex items-center justify-between gap-4">
              <span>{title}</span>
              {!isLoading && !loadError && (
                <div className="flex gap-2 me-6">
                  <button
                    aria-pressed={activeTab === 'view'}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      activeTab === 'view'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-default-100 hover:bg-default-200 text-foreground'
                    }`}
                    onClick={() => handleTabChange('view')}
                    type="button"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View
                  </button>
                  <button
                    aria-pressed={activeTab === 'edit'}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      activeTab === 'edit'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-default-100 hover:bg-default-200 text-foreground'
                    }`}
                    onClick={() => handleTabChange('edit')}
                    type="button"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit
                  </button>
                </div>
              )}
            </ModalHeader>
            <ModalBody>
              {isLoading && (
                <div className="flex items-center justify-center py-16">
                  <Spinner label="Loading prompt..." size="lg" />
                </div>
              )}

              {!!loadError && !isLoading && (
                <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
                  <p className="text-muted">Failed to load prompt</p>
                  <button
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                    onClick={onCancel}
                    type="button"
                  >
                    Close
                  </button>
                </div>
              )}

              {!isLoading && !loadError && (
                <>
                  {activeTab === 'view' ? (
                    <div className="prose dark:prose-invert max-w-none px-6 py-4 rounded-lg border border-default">
                      {localPrompt || prompt ? (
                        <Markdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ children }) => (
                              <h1 className="mb-2 mt-4 text-2xl font-bold text-foreground">
                                {children}
                              </h1>
                            ),
                            h2: ({ children }) => (
                              <h2 className="mb-2 mt-3 text-xl font-semibold text-foreground">
                                {children}
                              </h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="mb-1 mt-2 text-lg font-medium text-foreground">
                                {children}
                              </h3>
                            ),
                            p: ({ children }) => (
                              <p className="mb-2 leading-relaxed text-foreground">{children}</p>
                            ),
                            ul: ({ children }) => (
                              <ul className="mb-3 list-inside list-disc space-y-1">{children}</ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="mb-3 list-inside list-decimal space-y-1">
                                {children}
                              </ol>
                            ),
                            li: ({ children }) => <li className="text-foreground">{children}</li>,
                            strong: ({ children }) => (
                              <strong className="font-semibold">{children}</strong>
                            ),
                            em: ({ children }) => <em>{children}</em>,
                            code: ({ children }) => {
                              const str = String(children);
                              const isBlock = str.includes('\n');
                              if (isBlock) {
                                return (
                                  <pre className="my-0 overflow-x-auto rounded-lg bg-surface-elevated p-4 font-mono text-sm">
                                    <code>{children}</code>
                                  </pre>
                                );
                              }
                              return (
                                <code className="rounded bg-default-100 px-1 py-0.5 text-xs">
                                  {children}
                                </code>
                              );
                            },
                          }}
                        >
                          {localPrompt || prompt || ''}
                        </Markdown>
                      ) : (
                        <p className="text-muted">No prompt yet. Click Edit to add one.</p>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-default overflow-hidden">
                      <Textarea
                        ref={textareaRef}
                        name="prompt"
                        placeholder="Enter a prompt to provide guidance for AI agents working on this item..."
                        onValueChange={handleChangePrompt}
                        value={localPrompt}
                      />
                    </div>
                  )}

                  {/* Show PRD review option when PRD exists */}
                  {prdDocument && projectId && (
                    <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-primary/10 p-2">
                            <Bot className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">PRD Available</p>
                            <p className="text-xs text-muted">
                              Review and refine your PRD with AI assistance
                            </p>
                          </div>
                        </div>
                        <button
                          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                          onClick={handleReviewPRD}
                          type="button"
                        >
                          Review PRD with AI
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <button
                className="rounded px-4 py-2 text-sm font-medium text-secondary-600 hover:text-foreground dark:text-muted dark:hover:text-foreground"
                onClick={handleCloseRequest}
                type="button"
              >
                {isDirty ? 'Cancel' : 'Close'}
              </button>
              {!isLoading && !loadError && (
                <button
                  className="rounded bg-primary px-4 py-2 text-sm font-medium text-white transition-opacity hover:bg-primary/80 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary/90 dark:hover:bg-primary"
                  disabled={!isDirty}
                  onClick={handleSave}
                  type="button"
                >
                  Save
                </button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
}
